import React, { useEffect, useState } from 'react'
import {
  Dialog, DialogTitle, DialogContent, IconButton,
  useTheme, Zoom, Typography, Box, useMediaQuery, AppBar, Toolbar
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import NavButton from './NavButton';
import { TransitionProps } from '@mui/material/transitions';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';


export interface ChapterData {
  id: number;
  title: string;
  learnings: { content: string }[];
  locked: boolean;
  image?: string;
}

const TaskWrapper = ({ children }: { children: React.ReactNode }) => (
  <Box
    sx={{
      backgroundColor: '#f9f9f9',
      p: 3,
      borderRadius: 2,
      boxShadow: 1,
      mt: 1.5,
    }}
  >
    {children}
  </Box>
);

export default function ChapterSheet({
  chapter,
  open,
  onClose,
  onPrev,
  onNext,
  courseTitle
}: {
  chapter: ChapterData | null;
  open: boolean;
  onClose: () => void;
  onPrev?: () => void;
  onNext?: () => void;
  courseTitle?: string;
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const paperRef = React.useRef<HTMLDivElement>(null);
  const [pos, setPos] = React.useState<{ top: number; left: number; right: number }>();

  // Touch handling für Wischgesten
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);

  // Minimale Wischdistanz in px
  const minSwipeDistance = 50;

  const measure = React.useCallback(() => {
    if (!paperRef.current || isMobile) return;
    const r = paperRef.current.getBoundingClientRect();
    const offset = 24;
    setPos({
      top: r.top + r.height / 2,
      left: r.left - offset,
      right: window.innerWidth - r.right - offset,
    });
  }, [isMobile]);

  React.useLayoutEffect(measure, [measure]);

  useEffect(() => {
    if (courseTitle) {
      localStorage.setItem("currentCourseTitle", courseTitle);
    }
  }, [courseTitle]);

  const Transition = React.useMemo(
    () =>
      React.forwardRef(function MyZoom(
        { children, ...props }: TransitionProps & { children: React.ReactElement },
        ref
      ) {
        return (
          <Zoom
            {...props}
            ref={ref}
            onEntered={measure}
          >
            {children}
          </Zoom>
        );
      }),
    [measure]
  );

  React.useEffect(() => {
    if (!isMobile) {
      window.addEventListener('resize', measure);
      return () => window.removeEventListener('resize', measure);
    }
  }, [measure, isMobile]);

  React.useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft' && onPrev) onPrev();
      if (e.key === 'ArrowRight' && onNext) onNext();
    };
    if (!isMobile) {
      window.addEventListener('keydown', h);
      return () => window.removeEventListener('keydown', h);
    }
  }, [onPrev, onNext, isMobile]);

  // Android Back Button Handler - Alternative Lösung
  React.useEffect(() => {
    if (!isMobile || !open) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      // Fange verschiedene Back-Button Events ab
      if (e.key === 'Escape' || e.keyCode === 27) {
        e.preventDefault();
        onClose();
      }
    };

    // Verwende einen Timeout um sicherzustellen, dass der Event-Listener aktiv ist
    const timeoutId = setTimeout(() => {
      // Füge History-Eintrag hinzu
      const currentUrl = window.location.href;
      window.history.pushState({ modal: 'chapter', prev: currentUrl }, '');

      const handlePopState = () => {
        onClose();
      };

      window.addEventListener('popstate', handlePopState);
      window.addEventListener('keydown', handleKeyDown);

      // Cleanup in einem separaten Scope
      return () => {
        window.removeEventListener('popstate', handlePopState);
        window.removeEventListener('keydown', handleKeyDown);
      };
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isMobile, open, onClose]);

  // Touch Event Handler
  const onTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;

    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe && onNext) {
      onNext();
    }
    if (isRightSwipe && onPrev) {
      onPrev();
    }
  };

  if (!open || !chapter) return null;

  const renderContent = () => (
    <>
      {chapter.locked && (
        <Typography color="text.secondary">
          🔒 Dieses Kapitel ist noch nicht freigeschaltet.
        </Typography>
      )}

      {!chapter.locked && chapter.id === -2 && (
        <TaskWrapper>
          <ul style={{ paddingLeft: 20, lineHeight: 1.6, listStyleType: 'square' }}>
            {chapter.learnings.map((t, i) => (
              <li key={i} style={{ marginBottom: 8, fontSize: '1.05rem' }}>
                {t.content}
              </li>
            ))}
          </ul>
        </TaskWrapper>
      )}

      {!chapter.locked && chapter.id === -1 && (
        <TaskWrapper>
          <ul style={{ paddingLeft: 20, lineHeight: 1.6, listStyleType: 'square' }}>
            {chapter.learnings.map((t, i) => (
              <li key={i} style={{ marginBottom: 8, fontSize: '1.05rem' }}>
                {t.content}
              </li>
            ))}
          </ul>
        </TaskWrapper>
      )}

      {!chapter.locked && chapter.id !== -2 && chapter.id !== -1 && chapter.learnings.length > 0 && (
        <TaskWrapper>
          <List disablePadding sx={{
            my: -1,
            pl: 3,
            listStyleType: 'square',
            '& .MuiListItem-root': {
              display: 'list-item',
              py: 0.6,
            },
            '& .MuiListItemIcon-root': {
              display: 'none',
            },
          }}>
            {chapter.learnings.map((l, i) => (
              <ListItem
                key={i}
                disableGutters
                alignItems="flex-start"
                sx={{ py: 0.6 }}
              >
                <ListItemText
                  primary={l.content}
                  primaryTypographyProps={{
                    variant: 'body2',
                    sx: { lineHeight: 1.6 },
                  }}
                />
              </ListItem>
            ))}
          </List>
        </TaskWrapper>
      )}

      {!chapter.locked &&
        chapter.id !== -2 &&
        chapter.learnings.length === 0 && (
          <Typography color="text.secondary">
            Keine Learnings gefunden.
          </Typography>
        )}
    </>
  );

  // Mobile Vollbild-Ansicht
  if (isMobile) {
    return (
      <Dialog
        open={open}
        onClose={onClose}
        fullScreen
        TransitionComponent={Transition}
        PaperProps={{
          sx: {
            touchAction: 'pan-y', // Ermöglicht vertikales Scrollen, aber horizontales Wischen wird abgefangen
          }
        }}
      >
        <AppBar position="static" elevation={0} sx={{ bgcolor: 'background.paper', color: 'text.primary' }}>
          <Toolbar>
            <IconButton
              edge="start"
              color="inherit"
              onClick={onClose}
              aria-label="zurück"
            >
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h6" component="div" sx={{ flexGrow: 1, ml: 1 }}>
              {chapter.title}
            </Typography>
          </Toolbar>
        </AppBar>

        <DialogContent
          sx={{
            flex: 1,
            pt: 2,
            pb: 3,
            px: 2,
          }}
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
        >
          {renderContent()}


        </DialogContent>
      </Dialog>
    );
  }

  // Desktop-Ansicht (ursprünglicher Code)
  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        TransitionComponent={Transition}
        fullWidth
        maxWidth="md"
        PaperProps={{ ref: paperRef }}
      >
        <DialogTitle sx={{ pr: 6, fontSize: '1.5rem', fontWeight: 600 }}>
          {chapter.title}
          <IconButton onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{
          pt: 2,
          pb: 3,
          px: { xs: 2.5, sm: 4 },
          maxHeight: '75vh',
        }}>
          {renderContent()}
        </DialogContent>
      </Dialog>

      {pos && (
        <>
          <NavButton
            side="left"
            disabled={!onPrev}
            onClick={onPrev}
            top={pos.top}
            x={pos.left - 48}
          />
          <NavButton
            side="right"
            disabled={!onNext}
            onClick={onNext}
            top={pos.top}
            x={pos.right - 48}
          />
        </>
      )}
    </>
  );
}